import { useState, useEffect, useRef } from 'react';

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

const navLinks = [
  { label: 'Neden Cape Town', href: '#neden' },
  { label: 'Programlar', href: '#programlar' },
  { label: 'Konaklama', href: '#konaklama' },
  { label: 'Aktiviteler', href: '#aktiviteler' },
  { label: 'Vize', href: '#vize' },
  { label: 'SSS', href: '#sss' },
  { label: 'İletişim', href: '#form' },
];

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    age: '',
    level: '',
    goal: 'Genel İngilizce',
    duration: '',
    accommodation: 'Fark etmez',
    startDate: '',
    message: '',
    kvkk: false
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    setTimeout(() => setHeroLoaded(true), 100);
  }, []);

  // Scroll tracking for navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);

      // Determine active section
      const sectionIds = ['neden', 'programlar', 'konaklama', 'aktiviteler', 'vize', 'sss', 'form'];
      let current = '';
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120) {
            current = id;
          }
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const nedenRef = useInView(0.1);
  const programRef = useInView(0.1);
  const konaklamaRef = useInView(0.1);
  const aktiviteRef = useInView(0.1);
  const vizeRef = useInView(0.1);
  const sssRef = useInView(0.1);
  const formSectionRef = useInView(0.1);
  const footerRef = useInView(0.1);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.phone || !formData.email || !formData.kvkk) {
      setFormStatus('error');
      return;
    }

    try {
      const form = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      // Add all form fields
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('age', formData.age);
      formDataToSend.append('level', formData.level);
      formDataToSend.append('goal', formData.goal);
      formDataToSend.append('duration', formData.duration);
      formDataToSend.append('accommodation', formData.accommodation);
      formDataToSend.append('startDate', formData.startDate);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('kvkk', formData.kvkk ? 'Onaylandı' : 'Onaylanmadı');

      const response = await fetch('https://readdy.ai/api/form/d6fdfqn1jbcgr9kd6sqg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        // Reset form
        setFormData({
          name: '',
          phone: '',
          email: '',
          age: '',
          level: '',
          goal: 'Genel İngilizce',
          duration: '',
          accommodation: 'Fark etmez',
          startDate: '',
          message: '',
          kvkk: false
        });
        
        // Scroll to success message
        setTimeout(() => {
          form.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('error');
    }
  };

  const faqs = [
    {
      question: 'Hangi seviyeler kabul ediliyor?',
      answer: 'Beginner (A1)\'den Advanced (C2)\'ye kadar tüm seviyeler kabul edilir. Online seviye testi ile başlangıç seviyeniz belirlenir ve size uygun sınıfa yerleştirilirsiniz.'
    },
    {
      question: 'Haftada kaç ders seçebilirim?',
      answer: '20, 24, 26 veya 30 ders/hafta seçenekleri mevcuttur. İhtiyacınıza ve hedeflerinize göre yoğunluk belirleyebilirsiniz. Yoğun programlar daha hızlı ilerleme sağlar.'
    },
    {
      question: 'Konaklama seçenekleri neler?',
      answer: 'Aile yanı (homestay) ile günlük İngilizce pratiği ve kültürel deneyim, öğrenci rezidansı ile sosyal ortam ve bağımsızlık, veya özel apart seçenekleri sunuyoruz. Bütçenize göre en uygun seçeneği birlikte belirleriz.'
    },
    {
      question: 'Cape Town\'da sosyal hayat ve güvenlik nasıl?',
      answer: 'Cape Town çok kültürlü, dinamik ve güvenli bölgelere sahip bir şehirdir. Okul merkezi konumda, V&A Waterfront gibi güvenli alanlara yakındır. Haftalık sosyal aktivite programı ile şehri güvenle keşfedebilirsiniz.'
    },
    {
      question: '17 haftaya kadar vizesiz eğitim nasıl mümkün oluyor?',
      answer: 'Türkiye Cumhuriyeti umuma mahsus pasaport sahipleri Güney Afrika\'ya kısa süreli vize muafiyetinden yararlanabilir. Bu sayede 17 haftaya kadar vize başvurusu yapmadan Cape Town\'da İngilizce eğitimi alabilirsiniz. Pasaportunuzun geçerlilik süresi yeterli olmalıdır. Vize kuralları değişebileceğinden, güncel bilgi için formu doldurun; size özel yönlendirme yapalım.'
    },
    {
      question: '17 haftaya kadar eğitim planlarsam vize süreci nasıl?',
      answer: 'Türkiye Cumhuriyeti umuma mahsus pasaport sahipleri için kısa süreli vize muafiyeti mevcuttur. Ancak süre ve pasaport tipinize göre değişiklik gösterebilir. En doğru yönlendirme için formu doldurun, size özel bilgilendirme yapalım.'
    },
    {
      question: 'Ne kadar sürede akıcılık artar?',
      answer: 'İlerleme hızı başlangıç seviyenize ve program yoğunluğuna bağlıdır. Ortalama 8-12 haftalık yoğun programda bir seviye atlama beklenir. Günlük yaşamda İngilizce kullanımı ilerlemeyi hızlandırır.'
    }
  ];

  const baseAnim = 'transition-all duration-700 ease-out';
  const fadeUp = (visible: boolean) =>
    `${baseAnim} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`;
  const fadeLeft = (visible: boolean) =>
    `${baseAnim} ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`;
  const fadeRight = (visible: boolean) =>
    `${baseAnim} ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`;
  const scaleIn = (visible: boolean) =>
    `${baseAnim} ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`;

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <a
            href="https://www.steb.com.tr"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 cursor-pointer flex-shrink-0"
          >
            <span className={`text-2xl font-bold transition-all duration-300 ${scrolled ? 'text-[#007A4D]' : 'text-white'}`}>
              STEB
            </span>
          </a>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeSection === link.href.replace('#', '')
                    ? scrolled
                      ? 'bg-[#007A4D] text-white'
                      : 'bg-white/25 text-white'
                    : scrolled
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-white/80 hover:text-white hover:bg-white/15'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <a
            href="https://wa.me/905522827832"
            target="_blank"
            rel="noopener noreferrer"
            className={`hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'bg-[#007A4D] text-white hover:bg-[#006640]'
                : 'bg-white text-[#007A4D] hover:bg-white/90'
            }`}
          >
            <i className="ri-whatsapp-line text-lg"></i>
            WhatsApp
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
              scrolled ? 'text-gray-800 hover:bg-gray-100' : 'text-white hover:bg-white/15'
            }`}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-400 ease-in-out ${
            mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-white shadow-xl border-t border-gray-100 px-6 py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer ${
                  activeSection === link.href.replace('#', '')
                    ? 'bg-[#007A4D]/10 text-[#007A4D]'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://wa.me/905522827832"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mt-3 px-5 py-3 bg-[#007A4D] text-white font-semibold rounded-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              WhatsApp'tan Yaz
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative h-[90vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/75f4ce30477fb43cc745b1a31ddcd1a0.jpg"
            alt="Cape Town Table Mountain Waterfront"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
        </div>
        
        <div className="relative h-full flex items-center justify-center px-6">
          <div className="text-center max-w-4xl w-full">
            <div className={`inline-flex items-center gap-2 px-5 py-2 bg-white/20 backdrop-blur-sm border-2 border-white/40 rounded-full mb-8 ${baseAnim} ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-6'}`} style={{ transitionDelay: '200ms' }}>
              <span className="text-white text-sm font-light">🇿🇦 Güney Afrika'da İngilizce Eğitimi — 17 Haftaya Kadar Vizesiz</span>
            </div>
            
            <h1 className={`text-7xl font-extrabold text-white mb-6 leading-tight ${baseAnim} ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '400ms' }}>
              Güney Afrika'da İngilizce 17 Haftaya Kadar&nbsp;Vizesiz
            </h1>
            
            <p className={`text-xl text-white/90 font-light mb-10 leading-relaxed ${baseAnim} ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '600ms' }}>
              Vize derdi olmadan Cape Town'da doğa, şehir hayatı<br />
              ve uluslararası sınıflarda İngilizceyi hızlandırın
            </p>
            
            <div className={`flex items-center justify-center gap-4 mb-10 ${baseAnim} ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '800ms' }}>
              <div className="bg-white px-6 py-3 rounded-full">
                <span className="text-sm font-medium text-gray-800">✓ 17 Haftaya Kadar Vizesiz</span>
              </div>
              <div className="bg-white px-6 py-3 rounded-full">
                <span className="text-sm font-medium text-gray-800">📅 Yıl Boyu Başlangıç</span>
              </div>
              <div className="bg-white px-6 py-3 rounded-full">
                <span className="text-sm font-medium text-gray-800">🏠 Konaklama Dahil</span>
              </div>
            </div>
            
            <div className={`flex items-center justify-center gap-6 ${baseAnim} ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '1000ms' }}>
              <a
                href="https://wa.me/905522827832"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-10 py-4 bg-[#007A4D] text-white font-semibold rounded-full hover:bg-[#006640] transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                WhatsApp'tan Yaz
              </a>
              <a
                href="#form"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-white text-[#007A4D] font-semibold rounded-full hover:bg-gray-50 transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                Ücretsiz Teklif Al →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Neden Güney Afrika */}
      <section id="neden" className="py-24 bg-[#FFF9F0]">
        <div ref={nedenRef.ref} className="max-w-7xl mx-auto px-6">
          <div className={`mb-16 ${fadeLeft(nedenRef.isVisible)}`}>
            <p className="text-sm text-gray-500 font-light mb-2">Neden Cape Town?</p>
            <h2 className="text-5xl font-extrabold text-black leading-tight">
              İngilizce + Yaşam Deneyimi<br />
              Bir Arada
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: 'ri-chat-3-line', title: 'Resmi Dil', desc: 'İngilizce Güney Afrika\'nın resmi dillerinden biri. Eğitim ve iş dünyasında yaygın kullanım.' },
              { icon: 'ri-building-line', title: 'Cape Town Avantajı', desc: 'Çok kültürlü, dinamik şehir. Table Mountain ve sahillerle çevrili benzersiz konum.' },
              { icon: 'ri-heart-line', title: 'Kültür + Doğa', desc: 'Safari, penguen kolonileri, balina izleme. Eğitim + unutulmaz deneyimler.' }
            ].map((item, i) => (
              <div
                key={i}
                className={`bg-[#F5F5F5] rounded-2xl p-10 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${scaleIn(nedenRef.isVisible)}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className="w-16 h-16 flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-5xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Özeti */}
      <section id="programlar" className="py-24 bg-white">
        <div ref={programRef.ref} className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className={fadeLeft(programRef.isVisible)}>
              <img
                src="https://readdy.ai/api/search-image?query=diverse%20international%20students%20in%20modern%20bright%20classroom%20learning%20English%20with%20friendly%20teacher%2C%20multicultural%20group%2C%20interactive%20lesson%2C%20contemporary%20educational%20environment%2C%20natural%20lighting%2C%20professional%20education%20photography%2C%20warm%20atmosphere&width=800&height=900&seq=classroom-001&orientation=portrait"
                alt="Uluslararası Sınıf Ortamı"
                className="w-full h-[600px] object-cover rounded-3xl shadow-2xl"
              />
            </div>
            
            <div className={fadeRight(programRef.isVisible)}>
              <div className="inline-block px-4 py-2 bg-[#FFB612] text-black text-xs font-semibold rounded-full mb-6">
                PROGRAM DETAYLARI
              </div>
              
              <h2 className="text-5xl font-extrabold text-black mb-6 leading-tight">
                Esnek Program Seçenekleri<br />
                Her Seviyeye Uygun
              </h2>

              {/* 17 Hafta Vizesiz Vurgu Bandı */}
              <div className="mb-10 p-5 bg-gradient-to-r from-[#007A4D]/10 to-[#FFB612]/10 border-l-4 border-[#007A4D] rounded-r-xl">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#007A4D] rounded-full flex-shrink-0">
                    <i className="ri-shield-check-line text-base text-white"></i>
                  </div>
                  <span className="text-lg font-bold text-[#007A4D]">17 Haftaya Kadar Vizesiz Plan</span>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed ml-11">
                  Türk vatandaşları için en popüler seçenek: <strong>17 haftaya kadar vize başvurusu yapmadan</strong> Cape Town'da İngilizce eğitimi. Genel İngilizce, Yoğun veya İş İngilizcesi programlarını vizesiz süre içinde tamamlayabilirsiniz.
                </p>
              </div>
              
              <div className="space-y-6">
                {[
                  { icon: 'ri-calendar-line', title: 'Süre', desc: '1-52 hafta arası seçenekler (17 haftaya kadar vize muafiyeti)' },
                  { icon: 'ri-time-line', title: 'Yoğunluk', desc: '20 / 24 / 26 / 30 ders/hafta seçenekleri' },
                  { icon: 'ri-bar-chart-line', title: 'Seviyeler', desc: 'Beginner\'dan Advanced\'e tüm seviyeler' },
                  { icon: 'ri-book-open-line', title: 'Program Türleri', desc: 'Genel İngilizce, Yoğun, İş İngilizcesi, Uzun Dönem' },
                  { icon: 'ri-group-line', title: 'Sınıf Yapısı', desc: 'Ortalama 12, maksimum 15 kişi' },
                  { icon: 'ri-user-line', title: 'Özel Ders', desc: '60 dakika birebir ders seçeneği' }
                ].map((item, i) => (
                  <div key={i} className={`flex gap-4 ${fadeUp(programRef.isVisible)}`} style={{ transitionDelay: `${300 + i * 100}ms` }}>
                    <div className="w-12 h-12 flex items-center justify-center bg-[#007A4D] rounded-full flex-shrink-0">
                      <i className={`${item.icon} text-xl text-white`}></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-black mb-1">{item.title}</h4>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <a
                href="#form"
                className="inline-flex items-center justify-center gap-2 mt-10 px-8 py-4 bg-[#DE3831] text-white font-semibold rounded-full hover:bg-[#c42f29] transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                Programları İncele →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Konaklama */}
      <section id="konaklama" className="py-24 bg-[#E8F4F8]">
        <div ref={konaklamaRef.ref} className="max-w-7xl mx-auto px-6">
          <div className={`mb-16 ${fadeUp(konaklamaRef.isVisible)}`}>
            <p className="text-sm text-gray-500 font-light mb-2">Konaklama Seçenekleri</p>
            <h2 className="text-5xl font-extrabold text-black">Eviniz Cape Town'da</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                img: 'https://readdy.ai/api/search-image?query=warm%20welcoming%20homestay%20family%20dining%20together%20in%20cozy%20modern%20home%20interior%2C%20international%20student%20sharing%20meal%20with%20host%20family%2C%20friendly%20atmosphere%2C%20natural%20window%20light%2C%20comfortable%20living%20space%2C%20cultural%20exchange%20moment&width=600&height=400&seq=homestay-001&orientation=landscape',
                alt: 'Homestay Aile Yanı',
                title: 'Homestay - Aile Yanı',
                features: ['Ev yapımı öğünler', 'Günlük İngilizce pratiği', 'Kültürel deneyim']
              },
              {
                img: 'https://readdy.ai/api/search-image?query=modern%20student%20residence%20bedroom%20with%20desk%20and%20comfortable%20bed%2C%20bright%20clean%20contemporary%20dormitory%20room%2C%20minimalist%20design%2C%20natural%20daylight%2C%20organized%20study%20space%2C%20youth%20accommodation%2C%20fresh%20white%20walls&width=600&height=400&seq=residence-001&orientation=landscape',
                alt: 'Öğrenci Rezidansı',
                title: 'Öğrenci Rezidansı',
                features: ['Bağımsız yaşam', 'Sosyal ortam', 'Merkezi konum']
              },
              {
                img: 'https://readdy.ai/api/search-image?query=stylish%20studio%20apartment%20interior%20with%20kitchenette%20and%20living%20area%2C%20modern%20compact%20flat%20design%2C%20bright%20open%20space%2C%20contemporary%20furniture%2C%20city%20view%20window%2C%20independent%20living%20accommodation%2C%20clean%20minimalist%20aesthetic&width=600&height=400&seq=apartment-001&orientation=landscape',
                alt: 'Özel Apart',
                title: 'Özel Apart',
                features: ['Tam mahremiyet', 'Mutfak dahil', 'Esnek seçenekler']
              }
            ].map((item, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 ${scaleIn(konaklamaRef.isVisible)}`}
                style={{ transitionDelay: `${i * 200}ms` }}
              >
                <div className="h-64 w-full overflow-hidden">
                  <img
                    src={item.img}
                    alt={item.alt}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-black mb-4">{item.title}</h3>
                  <ul className="space-y-2 mb-6">
                    {item.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-gray-700">
                        <span className="text-[#007A4D] mt-1">•</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#form"
                    className="inline-block px-6 py-3 bg-white border-2 border-[#007A4D] text-[#007A4D] font-semibold rounded-full hover:bg-[#007A4D] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    Detaylar
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sosyal Aktiviteler */}
      <section id="aktiviteler" className="py-24 bg-white">
        <div ref={aktiviteRef.ref} className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 ${fadeUp(aktiviteRef.isVisible)}`}>
            <h2 className="text-5xl font-extrabold text-black mb-4">Cape Town'u Keşfedin</h2>
            <p className="text-lg text-gray-600">Haftalık sosyal aktivite programı dahil</p>
          </div>
          
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 ${fadeUp(aktiviteRef.isVisible)}`} style={{ transitionDelay: '200ms' }}>
            <div className="md:col-span-2 relative h-96 rounded-2xl overflow-hidden group cursor-pointer">
              <img
                src="https://readdy.ai/api/search-image?query=spectacular%20aerial%20view%20of%20Table%20Mountain%20cable%20car%20ascending%20with%20Cape%20Town%20city%20and%20ocean%20panorama%20below%2C%20dramatic%20mountain%20landscape%2C%20tourists%20enjoying%20scenic%20ride%2C%20blue%20sky%2C%20iconic%20landmark%2C%20professional%20travel%20photography&width=1200&height=600&seq=tablemountain-001&orientation=landscape"
                alt="Table Mountain"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <h3 className="text-3xl font-bold text-white">Table Mountain &amp; Signal Hill</h3>
              </div>
            </div>
            
            <div className="relative h-96 rounded-2xl overflow-hidden group cursor-pointer">
              <img
                src="https://readdy.ai/api/search-image?query=vibrant%20colorful%20houses%20of%20Bo%20Kaap%20neighborhood%20Cape%20Town%2C%20bright%20painted%20facades%20in%20rainbow%20colors%2C%20traditional%20Cape%20Malay%20quarter%2C%20cobblestone%20streets%2C%20cultural%20heritage%20site%2C%20sunny%20day%2C%20architectural%20photography&width=600&height=600&seq=bokaap-001&orientation=squarish"
                alt="Bo-Kaap"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <h3 className="text-2xl font-bold text-white">Bo-Kaap Kültür Bölgesi</h3>
              </div>
            </div>
          </div>
          
          <div className={`grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 ${fadeUp(aktiviteRef.isVisible)}`} style={{ transitionDelay: '400ms' }}>
            {[
              { img: 'https://readdy.ai/api/search-image?query=bustling%20Victoria%20and%20Alfred%20Waterfront%20harbor%20with%20boats%20and%20shops%2C%20modern%20shopping%20district%2C%20Cape%20Town%20marina%2C%20tourists%20walking%2C%20mountain%20backdrop%2C%20vibrant%20atmosphere%2C%20daytime%20scene&width=500&height=500&seq=waterfront-001&orientation=squarish', alt: 'V&A Waterfront', title: 'V&A Waterfront' },
              { img: 'https://readdy.ai/api/search-image?query=adorable%20African%20penguins%20colony%20at%20Boulders%20Beach%20Cape%20Town%2C%20cute%20penguins%20on%20white%20sand%20beach%2C%20crystal%20clear%20turquoise%20water%2C%20natural%20habitat%2C%20wildlife%20photography%2C%20sunny%20coastal%20scene&width=500&height=500&seq=penguins-001&orientation=squarish', alt: 'Boulders Beach Penguenler', title: 'Boulders Beach' },
              { img: 'https://readdy.ai/api/search-image?query=stunning%20Clifton%20Beach%20Cape%20Town%20with%20white%20sand%20and%20turquoise%20ocean%2C%20luxury%20beach%20scene%2C%20granite%20boulders%2C%20sunbathers%2C%20pristine%20coastline%2C%20mountain%20backdrop%2C%20perfect%20weather%2C%20coastal%20paradise&width=500&height=500&seq=clifton-001&orientation=squarish', alt: 'Clifton Beach', title: 'Clifton Beach' },
              { img: 'https://readdy.ai/api/search-image?query=dramatic%20Cape%20Point%20lighthouse%20on%20rocky%20cliff%20edge%2C%20southern%20tip%20of%20Africa%2C%20ocean%20meeting%20point%2C%20rugged%20coastal%20landscape%2C%20scenic%20viewpoint%2C%20wild%20nature%2C%20epic%20seascape%20photography&width=500&height=500&seq=capepoint-001&orientation=squarish', alt: 'Cape Point', title: 'Cape Point' }
            ].map((item, i) => (
              <div key={i} className="relative h-72 rounded-2xl overflow-hidden group cursor-pointer">
                <img src={item.img} alt={item.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <h4 className="text-lg font-bold text-white">{item.title}</h4>
                </div>
              </div>
            ))}
          </div>
          
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${fadeUp(aktiviteRef.isVisible)}`} style={{ transitionDelay: '600ms' }}>
            <div className="relative h-80 rounded-2xl overflow-hidden group cursor-pointer">
              <img
                src="https://readdy.ai/api/search-image?query=majestic%20Big%20Five%20safari%20scene%20with%20elephants%20lions%20and%20rhinos%20in%20African%20savanna%2C%20golden%20grassland%20landscape%2C%20wildlife%20adventure%2C%20South%20Africa%20game%20reserve%2C%20dramatic%20sunset%20lighting%2C%20professional%20nature%20photography&width=800&height=500&seq=safari-001&orientation=landscape"
                alt="Safari Big Five"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <h3 className="text-2xl font-bold text-white">Haftasonu Safari Turları</h3>
              </div>
            </div>
            
            <div className="relative h-80 rounded-2xl overflow-hidden group cursor-pointer">
              <img
                src="https://readdy.ai/api/search-image?query=magnificent%20humpback%20whale%20breaching%20in%20ocean%20near%20Cape%20Town%20coast%2C%20whale%20watching%20tour%20boat%2C%20dramatic%20water%20splash%2C%20marine%20wildlife%2C%20blue%20ocean%2C%20spectacular%20nature%20moment%2C%20wildlife%20photography&width=800&height=500&seq=whale-001&orientation=landscape"
                alt="Balina İzleme"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <h3 className="text-2xl font-bold text-white">Balina İzleme (Sezonluk)</h3>
              </div>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <div className="inline-block px-6 py-3 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600">⚠️ Aktiviteler sezona göre değişiklik gösterebilir</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vize Bilgisi */}
      <section id="vize" className="py-20 bg-gradient-to-br from-[#FFB612] to-[#FFA500]">
        <div ref={vizeRef.ref} className={`max-w-4xl mx-auto px-6 text-center ${fadeUp(vizeRef.isVisible)}`}>
          <div className={`w-16 h-16 flex items-center justify-center mx-auto mb-6 ${scaleIn(vizeRef.isVisible)}`}>
            <i className="ri-passport-line text-6xl text-gray-800"></i>
          </div>
          
          <h2 className="text-4xl font-extrabold text-black mb-4">Vize ve Süre Bilgilendirmesi</h2>
          
          <div className={`inline-flex items-center gap-3 px-8 py-4 bg-[#007A4D] rounded-full mb-8 ${scaleIn(vizeRef.isVisible)}`} style={{ transitionDelay: '200ms' }}>
            <i className="ri-shield-check-line text-2xl text-white"></i>
            <span className="text-xl font-bold text-white whitespace-nowrap">17 Haftaya Kadar Vizesiz Eğitim İmkânı</span>
          </div>
          
          <div className={`bg-white rounded-2xl p-10 text-left space-y-6 ${fadeUp(vizeRef.isVisible)}`} style={{ transitionDelay: '300ms' }}>
            <div className="flex items-start gap-4 p-5 bg-[#F0FFF4] border border-[#007A4D]/20 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center bg-[#007A4D] rounded-full flex-shrink-0">
                <i className="ri-check-line text-xl text-white"></i>
              </div>
              <div>
                <p className="text-lg font-bold text-[#007A4D] mb-1">17 Haftaya Kadar Vizesiz</p>
                <p className="text-gray-700 leading-relaxed">
                  Türkiye Cumhuriyeti umuma mahsus pasaport sahipleri için kısa süreli vize muafiyeti mevcuttur. <strong>17 haftaya kadar vize başvurusu yapmadan</strong> Cape Town'da İngilizce eğitimi alabilirsiniz.
                </p>
              </div>
            </div>
            
            <p className="text-lg text-gray-800 leading-relaxed">
              ⚠️ Uzun süreli eğitim planlarında (17+ hafta) vize/uzatma gerekebilir. Süre ve pasaport tipinize göre değişiklik gösterebilir.
            </p>
            <p className="text-lg text-gray-800 leading-relaxed">
              📋 Vize kuralları değişebilir; nihai karar resmi makamlara aittir. En güncel ve doğru bilgi için mutlaka yetkili makamlarla iletişime geçilmelidir.
            </p>
          </div>
          
          <a
            href="#form"
            className="inline-block mt-8 px-10 py-4 bg-[#007A4D] text-white font-semibold rounded-full hover:bg-[#006640] transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            Sürenize Özel Vize Danışmanlığı Alın
          </a>
        </div>
      </section>

      {/* SSS */}
      <section id="sss" className="py-24 bg-[#F8F8F8]">
        <div ref={sssRef.ref} className="max-w-4xl mx-auto px-6">
          <h2 className={`text-5xl font-extrabold text-black text-center mb-16 ${fadeUp(sssRef.isVisible)}`}>Sıkça Sorulan Sorular</h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl overflow-hidden ${fadeUp(sssRef.isVisible)}`}
                style={{ transitionDelay: `${150 + index * 80}ms` }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                >
                  <span className="text-xl font-semibold text-black pr-4">{faq.question}</span>
                  <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}>
                    <i className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-2xl text-[#007A4D]`}></i>
                  </div>
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed mb-4">{faq.answer}</p>
                    <a
                      href="#form"
                      className="inline-flex items-center text-[#007A4D] font-semibold hover:underline cursor-pointer"
                    >
                      Detaylı bilgi al →
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* İletişim Formu - already has id="form" */}
      <section id="form" className="py-24 bg-gradient-to-br from-[#002395] to-[#001a6e]">
        <div ref={formSectionRef.ref} className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className={`lg:col-span-2 ${fadeLeft(formSectionRef.isVisible)}`}>
              <h2 className="text-5xl font-extrabold text-white mb-6 leading-tight">
                Cape Town Planınızı<br />
                Birlikte Oluşturalım
              </h2>
              <p className="text-lg text-white/80 mb-10 leading-relaxed">
                2 dakikada ön değerlendirme formu doldurun, 24 saat içinde size özel teklif alalım.
              </p>
              <a
                href="https://wa.me/905522827832"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] text-white font-semibold rounded-full hover:bg-[#20bd5a] transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                WhatsApp: +90 552 282 78 32
              </a>
            </div>
            
            <div className={`lg:col-span-3 ${fadeRight(formSectionRef.isVisible)}`}>
              <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-10 shadow-2xl" data-readdy-form>
                {formStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 font-semibold">✓ Formunuz başarıyla gönderildi! 24 saat içinde size dönüş yapacağız.</p>
                  </div>
                )}
                
                {formStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 font-semibold">✗ Bir hata oluştu. Lütfen tekrar deneyin.</p>
                  </div>
                )}
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Ad Soyad *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A4D] focus:border-transparent outline-none transition-all text-sm"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A4D] focus:border-transparent outline-none transition-all text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">E-posta *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A4D] focus:border-transparent outline-none transition-all text-sm"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Yaş</label>
                      <select
                        name="age"
                        value={formData.age}
                        onChange={(e) => setFormData({...formData, age: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A4D] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                      >
                        <option value="">Seçiniz</option>
                        <option value="16-20">16-20</option>
                        <option value="21-25">21-25</option>
                        <option value="26-30">26-30</option>
                        <option value="31+">31+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">İngilizce Seviyeniz</label>
                      <select
                        name="level"
                        value={formData.level}
                        onChange={(e) => setFormData({...formData, level: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A4D] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                      >
                        <option value="">Seçiniz</option>
                        <option value="A1">A1 - Başlangıç</option>
                        <option value="A2">A2 - Temel</option>
                        <option value="B1">B1 - Orta Alt</option>
                        <option value="B2">B2 - Orta Üst</option>
                        <option value="C1">C1 - İleri</option>
                        <option value="C2">C2 - Çok İleri</option>
                        <option value="Bilmiyorum">Bilmiyorum</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">Hedefiniz</label>
                    <div className="space-y-2">
                      {['Genel İngilizce', 'İş İngilizcesi', 'Uzun Dönem'].map((g) => (
                        <label key={g} className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="goal"
                            value={g}
                            checked={formData.goal === g}
                            onChange={(e) => setFormData({...formData, goal: e.target.value})}
                            className="w-4 h-4 text-[#007A4D] cursor-pointer"
                          />
                          <span className="ml-3 text-sm text-gray-700">{g}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Eğitim Süresi</label>
                      <select
                        name="duration"
                        value={formData.duration}
                        onChange={(e) => setFormData({...formData, duration: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A4D] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                      >
                        <option value="">Seçiniz</option>
                        <option value="1-4 hafta">1-4 hafta</option>
                        <option value="5-8 hafta">5-8 hafta</option>
                        <option value="9-17 hafta">9-17 hafta</option>
                        <option value="18+ hafta">18+ hafta</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Başlangıç Tarihi</label>
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A4D] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">Konaklama Tercihi</label>
                    <div className="space-y-2">
                      {[
                        { value: 'Aile yanı', label: 'Aile yanı (Homestay)' },
                        { value: 'Rezidans', label: 'Öğrenci Rezidansı' },
                        { value: 'Fark etmez', label: 'Fark etmez' }
                      ].map((a) => (
                        <label key={a.value} className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="accommodation"
                            value={a.value}
                            checked={formData.accommodation === a.value}
                            onChange={(e) => setFormData({...formData, accommodation: e.target.value})}
                            className="w-4 h-4 text-[#007A4D] cursor-pointer"
                          />
                          <span className="ml-3 text-sm text-gray-700">{a.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Mesajınız</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({...formData, message: e.target.value});
                        }
                      }}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A4D] focus:border-transparent outline-none transition-all resize-none text-sm"
                      placeholder="Sorularınızı veya özel taleplerinizi yazabilirsiniz..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 karakter</p>
                  </div>
                  
                  <div>
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        name="kvkk"
                        checked={formData.kvkk}
                        onChange={(e) => setFormData({...formData, kvkk: e.target.checked})}
                        className="w-4 h-4 text-[#007A4D] mt-1 cursor-pointer"
                        required
                      />
                      <span className="ml-3 text-xs text-gray-600 leading-relaxed">
                        Kişisel verilerimin işlenmesine ve tarafıma bilgilendirme yapılmasına onay veriyorum. <a href="#" className="text-[#007A4D] underline">KVKK Aydınlatma Metni</a>
                      </span>
                    </label>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full py-4 bg-[#DE3831] text-white font-bold text-lg rounded-full hover:bg-[#c42f29] transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                  >
                    Ücretsiz Teklif Al →
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* STEB Bilgileri + Harita */}
      <section className="py-20 bg-[#007A4D]">
        <div ref={footerRef.ref} className={`max-w-7xl mx-auto px-6 ${fadeUp(footerRef.isVisible)}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-white text-2xl font-bold mb-6">STEB YURT DIŞI EĞİTİM</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Yurt dışı eğitim danışmanlığında<br />
                güvenilir çözüm ortağınız.<br />
                Ankara merkezli, Türkiye çapında hizmet.
              </p>
            </div>
            
            <div>
              <h3 className="text-white text-sm font-bold uppercase mb-4 tracking-wider">İLETİŞİM</h3>
              <div className="space-y-3 text-white text-base">
                <p className="flex items-start gap-2">
                  <i className="ri-map-pin-line mt-1 flex-shrink-0"></i>
                  <span>Bestekar Cd 78/11, Çankaya 06680 Ankara</span>
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-phone-line flex-shrink-0"></i>
                  <a href="tel:+903124178811" className="hover:underline">+90 312 417 88 11</a>
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-smartphone-line flex-shrink-0"></i>
                  <a href="https://wa.me/905522827832" className="hover:underline">+90 552 282 78 32</a>
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-mail-line flex-shrink-0"></i>
                  <a href="mailto:info@steb.com.tr" className="hover:underline">info@steb.com.tr</a>
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-global-line flex-shrink-0"></i>
                  <a href="https://www.steb.com.tr" target="_blank" rel="noopener noreferrer" className="hover:underline">www.steb.com.tr</a>
                </p>
              </div>
            </div>
            
            <div>
              <h3 className="text-white text-sm font-bold uppercase mb-4 tracking-wider">HIZLI ERİŞİM</h3>
              <div className="space-y-2">
                <a href="https://steb.com.tr/neden-steb" target="_blank" rel="noopener noreferrer" className="block text-white hover:text-white/80 transition-colors">• Hakkımızda</a>
                <a href="#" className="block text-white hover:text-white/80 transition-colors">• Diğer Destinasyonlar</a>
                <a href="#" className="block text-white hover:text-white/80 transition-colors">• Blog</a>
                <a href="#form" className="block text-white hover:text-white/80 transition-colors">• İletişim</a>
              </div>
            </div>
          </div>
          
          <div className="rounded-2xl overflow-hidden h-96">
            <iframe
              src="https://www.google.com/maps?q=39.907364,32.859681&z=17&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="STEB Yurt Dışı Eğitim Danışmanlık Konumu"
            ></iframe>
          </div>

          <div className="mt-8 pt-6 border-t border-white/20 text-center">
            <p className="text-white/60 text-sm">© 2025 STEB Yurt Dışı Eğitim Danışmanlık. Tüm hakları saklıdır. | <a href="https://readdy.ai/?ref=logo" rel="nofollow" className="hover:text-white transition-colors">Powered by Readdy</a></p>
          </div>
        </div>
      </section>

      {/* Sticky CTA Band */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#FFB612] shadow-2xl z-50 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="text-lg font-bold text-black hidden md:block">Cape Town'da İngilizce planını netleştirelim</p>
          <a
            href="#form"
            onClick={(e) => { e.preventDefault(); handleNavClick('#form'); }}
            className="px-8 py-3 bg-[#007A4D] text-white font-semibold rounded-full hover:bg-[#006640] transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap md:ml-auto"
          >
            2 Dakikada Form Doldur →
          </a>
        </div>
      </div>

      {/* Bottom Padding for Sticky Band */}
      <div className="h-20"></div>
    </div>
  );
}
